import React, { useCallback, useEffect, useState } from 'react';
import { Combobox, FormField } from '@vwfs-bronson/bronson-react';
import { Contract } from 'cpnl-common';
import { useTranslation } from 'react-i18next';
import { ContractSelectItem } from 'components/common';
import { ComboboxState, ComboboxOption } from '../../../../types/bronson-react';
import { useField } from 'formik';

type ContractSelectProps = {
    label: string;
    name: string;
    contracts: Contract[] | undefined;
    unpaidBalanceContract?: string;
};

export const ContractSelect: React.FC<ContractSelectProps> = ({
    label,
    contracts = [],
    name,
    unpaidBalanceContract,
}) => {
    const { t } = useTranslation('request');
    const [, , helper] = useField(name);

    const [selectedOption, setSelectedOption] = useState<ComboboxOption[]>([]);

    const noRelatedContractOption: ComboboxOption = {
        key: 'noRelatedContract',
        value: {
            textValue: t(`contract-options.no-related-contract`),
            content: <>{t(`contract-options.no-related-contract`)}</>,
        },
    };

    const contractData: ComboboxOption[] = [
        noRelatedContractOption,
        ...contracts.map((contract) => ({
            key: contract.contractNumber,
            value: { textValue: contract.contractNumber, content: <ContractSelectItem contract={contract} /> },
        })),
    ];

    const initalOption = unpaidBalanceContract ? unpaidBalanceContract : 'noRelatedContract';

    const getDefaultState = (): ComboboxState => {
        const defaultOption = contractData.filter((x) => x.key === initalOption)[0] || noRelatedContractOption;
        return {
            value: [
                {
                    key: defaultOption.key,
                    value: defaultOption.value,
                },
            ],
        };
    };

    useEffect(() => {
        helper.setValue(getDefaultState().value[0].value);
        // eslint-disable-next-line
    }, []);

    const internalOnChange = useCallback((options: { key: string; value: string }[]): void => {
        setSelectedOption(options);
        const values: string[] = options.map((option) => option.value);

        helper.setValue(values[0]);
        // eslint-disable-next-line
    }, []);

    return (
        <FormField labelText={label} type="select" className="c-form-field--no-floating">
            <Combobox
                required
                testId={name}
                defaultState={getDefaultState()}
                onChange={internalOnChange}
                value={selectedOption}
                options={contractData}
            />
        </FormField>
    );
};
